import React, { useMemo, useState } from 'react';
import PageHeader from '../../components/shared/PageHeader';
import DataToolbar, { StatusPill, Card } from '../../components/shared/DataToolbar';
import { LoadingState, ErrorState, EmptyResults } from '../../components/shared/Pending';
import { CalendarDays, Loader2, Mail, Phone, Users } from 'lucide-react';
import { useApi } from '../../api/useApi';
import { getAdminCustomer, listAdminCustomers } from '../../api/adminApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const money = (value, currency = 'USD') => {
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(value || 0)); }
  catch (_) { return `$${Number(value || 0).toFixed(2)}`; }
};

export default function AdminCustomers() {
  const { data, loading, error, refetch } = useApi(() => listAdminCustomers({ per_page: 100 }), []);
  const customers = useMemo(() => Array.isArray(data) ? data : (data?.items || data || []), [data]);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const filtered = customers.filter((customer) => !q || `${customer.name || ''} ${customer.email || ''} ${customer.phone || ''}`.toLowerCase().includes(q.toLowerCase()));

  const openCustomer = async (customer) => {
    setSelected({ customer, bookings: [] });
    setDetailLoading(true);
    try {
      const detail = await getAdminCustomer(customer.id);
      setSelected(detail || { customer, bookings: [] });
    } catch (_) {
      setSelected({ customer, bookings: [] });
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div>
      <PageHeader title="Customers" description="All guest accounts and booking activity across StayEasy." />
      <DataToolbar search={q} onSearch={setQ} placeholder="Search by name, email or phone…" />

      {loading ? <LoadingState label="Loading customers…" />
        : error ? <ErrorState error={error} onRetry={refetch} />
        : filtered.length === 0 ? <EmptyResults icon={Users} title="No customers found" description="No guest accounts match this search." />
        : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((customer) => (
              <button key={customer.id} type="button" onClick={() => openCustomer(customer)} className="text-left">
                <Card className="p-5 h-full card-hover">
                  <div className="flex items-start gap-3">
                    {customer.avatar ? (
                      <img src={customer.avatar} className="h-12 w-12 rounded-full object-cover" alt="" />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-slate-100 text-slate-500 grid place-items-center font-semibold">{(customer.name || customer.email || '?').slice(0, 1).toUpperCase()}</div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{customer.name || 'Unnamed guest'}</div>
                      <div className="text-xs text-slate-500 truncate">{customer.email}</div>
                      {customer.phone && <div className="text-xs text-slate-400 truncate mt-0.5">{customer.phone}</div>}
                    </div>
                    <StatusPill status={customer.email_verified_at ? 'verified' : 'unverified'} />
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500">Bookings</div><div className="text-lg font-bold text-slate-900 tabular-nums">{customer.total_bookings ?? 0}</div></div>
                    <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500">Confirmed spend</div><div className="text-lg font-bold text-slate-900 tabular-nums">{money(customer.total_spent)}</div></div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 text-xs text-teal-700 font-medium">View customer history</div>
                </Card>
              </button>
            ))}
          </div>
        )}

      <CustomerDetail value={selected} loading={detailLoading} onOpenChange={(open) => { if (!open) setSelected(null); }} />
    </div>
  );
}

function CustomerDetail({ value, loading, onOpenChange }) {
  const customer = value?.customer;
  const bookings = value?.bookings || [];
  return (
    <Dialog open={Boolean(value)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto">
        {customer && (
          <>
            <DialogHeader>
              <div className="flex items-center justify-between gap-4">
                <DialogTitle>{customer.name || 'Customer details'}</DialogTitle>
                {loading && <span className="inline-flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading history</span>}
              </div>
            </DialogHeader>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg bg-slate-50 p-4"><div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500"><Mail className="h-3.5 w-3.5" /> Email</div><div className="mt-2 text-sm font-medium text-slate-900 break-all">{customer.email || '—'}</div></div>
              <div className="rounded-lg bg-slate-50 p-4"><div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500"><Phone className="h-3.5 w-3.5" /> Phone</div><div className="mt-2 text-sm font-medium text-slate-900">{customer.phone || '—'}</div></div>
            </div>
            <div className="mt-6 flex items-center justify-between"><h3 className="font-semibold text-slate-900">Booking history</h3><span className="text-xs text-slate-500">{bookings.length} booking{bookings.length === 1 ? '' : 's'}</span></div>
            <div className="mt-3 divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
              {!loading && bookings.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No booking history available.</div>}
              {bookings.map((booking) => (
                <div key={booking.id} className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1"><div className="font-medium text-slate-900 truncate">{booking.property?.title || `Booking #${booking.id}`}</div><div className="mt-1 inline-flex items-center gap-1.5 text-xs text-slate-500"><CalendarDays className="h-3.5 w-3.5" /> {booking.check_in} → {booking.check_out}</div><div className="mt-1 text-xs text-slate-400">Host: {booking.property?.host?.name || '—'}</div></div>
                  <div className="sm:text-right"><StatusPill status={booking.status} /><div className="mt-1 text-sm font-semibold text-slate-900">{money(booking.total_amount, booking.currency)}</div></div>
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
